<template>
  <div
    class="bg-white rounded-xl relative w-full transition-all duration-300 ease-in-out z-20 flex flex-col"
  >
    <!-- Card image -->
    <div
      :style="{ backgroundImage: 'url(' + featured_image + ')' }"
      :class="[
        post.category === 3 ? 'card-img' : '',
        post.event_invalid ? 'invalid' : '',
      ]"
      class="rounded-t-xl overflow-hidden bg-cover bg-center relative h-48 w-full text-white flex items-center justify-center flex-col"
    >
      <div
        v-if="post.category === 3 && start_date_short"
        class="text-center flex items-center justify-center leading-none z-40"
        :class="start_date_short === end_date_short ? 'text-2xl' : 'text-xl'"
      >
        <span class="font-primary font-semibold">{{ start_date_short }}</span>
        <span
          v-if="start_date_short !== end_date_short"
          :class="['ml-2 font-primary font-semibold']"
          >- {{ end_date_short }}</span
        >
      </div>

      <div v-if="post.event_invalid" class="text-center z-40 px-6">
        <p class="text-red-500 font-bold">Invalid Event!</p>
        <p class="text-sm">
          This event does not contain a date. It will be hidden from your users
          until a date is added.
        </p>
      </div>
    </div>
    <!-- /Card Image -->

    <!-- Card Content -->
    <div class="relative p-6 pb-3 flex flex-col justify-between flex-grow">
      <div>
        <h3 class="font-bold text-base">{{ post.title }}</h3>
      </div>

      <!-- Card base -->
      <div class="mt-auto pt-3 flex items-center justify-between">
        <p
          :id="post.id"
          class="z-40 pb-1 border-b border-truegray-300 w-max cursor-pointer hover:text-primary"
          @click="viewPost(post.id)"
        >
          View post >
        </p>
        <div
          class="rounded-full flex items-center justify-center text-white w-10 h-10 mr-[-12px]"
          :class="tagDetails.bgClass"
        >
          <GeneralTagIcons size="26px" :tag="tagDetails.text" />
        </div>
      </div>
    </div>
    <!-- /Card Content -->
  </div>
</template>

<script>
import { defineComponent, onMounted, computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router/composables'
import { useStore } from '../../../../../../js/store/index.js'
import GeneralTagIcons from '../../../../../../shared/components/GeneralTagIcons.vue'

export default defineComponent({
  name: 'PostCardExplore',
  components: { GeneralTagIcons },

  props: {
    post: {
      type: Object,
      required: true,
    },
  },

  setup(props) {
    const router = useRouter()
    const store = useStore()
    const customisation = computed(
      () => store.getters['careerwise/getCustomisationForUi'],
    )

    const tagDetails = computed(() => {
      // Check if post has tags and customisation has contentTags
      if (props.post.tags && customisation.value.contentTags) {
        // Find a matching tag
        const matchingTag = customisation.value.contentTags.find((contentTag) =>
          props.post.tags.some((tag) => contentTag.value === tag.id),
        )

        // If a matching tag is found, return its title and bgClass
        if (matchingTag) {
          return {
            text: matchingTag.text,
            bgClass: matchingTag.bgClass,
          }
        }
      }

      // If no matching tag is found, check the post category
      const postType = customisation.value.contentPostTypes.find(
        (type) => type.value === props.post.category,
      )

      if (postType) {
        return {
          text: postType.text,
          bgClass: postType.bgClass,
        }
      }

      // Default values
      return {
        title: '',
        bgClass: '',
      }
    })

    const start_date_short = ref(null)
    const end_date_short = ref(null)
    const featured_image = ref('')

    const updatePostDetails = (val) => {
      if (val.event_start_at_ui) {
        start_date_short.value = val.event_start_at_ui.split(',')[0]
        end_date_short.value = val.event_end_at_ui.split(',')[0]
      }
      if (val.image_featured) {
        featured_image.value = val.image_featured
      } else {
        switch (val.category) {
          case 1:
            featured_image.value = '/img/default-news.jpg'
            break
          case 2:
            featured_image.value = '/img/default-job.png'
            break
          case 3:
            featured_image.value = '/img/default-event.png'
            break
          default:
            break
        }
      }
    }

    const viewPost = (id) => {
      router.push({ path: `/post/${id}` })
    }

    watch(
      props.post,
      (val) => {
        updatePostDetails(val)
      },
      { deep: true },
    )

    onMounted(() => {
      updatePostDetails(props.post)
    })

    return {
      customisation,
      tagDetails,

      start_date_short,
      end_date_short,
      featured_image,

      viewPost,
    }
  },
})
</script>

<style scoped>
.card-img::after {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: #000;
  opacity: 0.6;
  pointer-events: none;
}

.invalid.card-img::after {
  opacity: 0.7;
}
</style>
